import React, { useState, useCallback, useMemo, useEffect } from 'react'
import ModalContainer from '../modals/ModalContainer'
import MerchantProductService from '../../../app/modules/merchant-product/services/merchantProductService'
import type { MerchantProductBrowserModalProps, MerchantProduct } from './types'

// Transform API product to modal format - simplified for essential fields only
const transformApiProduct = (apiProduct: any): MerchantProduct => {
  const primaryImage = apiProduct.photos?.find((photo: any) => photo.isPrimary)?.url || apiProduct.mainImage
  
  return {
    ...apiProduct,
    // Map to display-friendly format - only essential fields
    image: primaryImage,
    price: apiProduct.price ? `Rp ${apiProduct.price.toLocaleString('id-ID')}` : 'Price N/A',
    stock: apiProduct.stock || 0,
  }
}

const MerchantProductBrowserModal: React.FC<MerchantProductBrowserModalProps> = ({
  isOpen,
  onClose,
  onSelectProduct,
  onCopyProduct,
  mode,
  title,
  className = '',
  showBackButton = false,
  onBack,
  linkTarget
}) => {
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [products, setProducts] = useState<MerchantProduct[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasSearched, setHasSearched] = useState(false)
  const [viewMode, setViewMode] = useState<'row' | 'grid'>('row')

  const merchantProductService = useMemo(() => MerchantProductService.getInstance(), [])

  // Search products when query changes (debounced)
  const searchProducts = useCallback(async (query: string) => {
    if (!query.trim()) {
      setProducts([])
      setHasSearched(false)
      return
    }

    setLoading(true)
    setError(null)
    setHasSearched(true)
    
    try {
      const response = await merchantProductService.searchProducts(query.trim(), 1, 50)
      const transformedProducts = response.data.product_merchants.map(transformApiProduct)
      setProducts(transformedProducts)
    } catch (err) {
      console.error('Failed to search products:', err)
      setError('Failed to search products. Please try again.')
      setProducts([])
    } finally {
      setLoading(false)
    }
  }, [merchantProductService])

  // Debounce search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchProducts(searchQuery)
    }, 300)
    
    return () => clearTimeout(timeoutId)
  }, [searchQuery, searchProducts])

  // Get selected product
  const selectedProduct = useMemo(() => {
    return products.find(p => p.id === selectedProductId)
  }, [selectedProductId, products])

  // Reset modal state when opening
  useEffect(() => {
    if (isOpen) {
      setSelectedProductId(null)
      setSearchQuery('')
      setProducts([])
      setLoading(false)
      setError(null)
      setHasSearched(false)
    }
  }, [isOpen])

  // Handle ESC key
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscapeKey)
    return () => {
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [isOpen, onClose])

  // Handle product selection
  const handleSelectProduct = useCallback((product: MerchantProduct) => {
    setSelectedProductId(product.id)
  }, [])

  // Handle confirm selection
  const handleConfirmSelection = useCallback(() => {
    console.log("🎯 handleConfirmSelection called with:", { selectedProduct, mode });
    if (selectedProduct) {
      if (mode === 'link') {
        console.log("🔗 Calling onSelectProduct for link mode");
        onSelectProduct(selectedProduct)
      } else {
        console.log("📋 Calling onCopyProduct for copy mode");
        onCopyProduct(selectedProduct)
      }
      console.log("🚪 Closing modal");
      onClose()
    } else {
      console.warn("⚠️ No product selected for confirmation");
    }
  }, [selectedProduct, mode, onSelectProduct, onCopyProduct, onClose])

  // Handle back button
  const handleBack = useCallback(() => {
    if (onBack) {
      onBack()
    } else {
      onClose()
    }
  }, [onBack, onClose])

  if (!isOpen) return null

  const modalTitle = title || (mode === 'link' ? 'Link to Merchant Product' : 'Select Merchant Product')
  const modalDescription = mode === 'link' 
    ? 'Select a merchant product to generate SKU and create product link.'
    : 'Choose a product to copy as a starting point for your new listing.'

  // Custom header content with search controls
  const customHeaderContent = (
    <>
      {/* Search Input */}
      <div className="tw-relative">
        <input 
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search products by name or SKU..."
          className="tw-w-full tw-pl-10 tw-pr-4 tw-py-2.5 tw-border tw-border-gray-300 tw-rounded-lg focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent tw-bg-white tw-text-gray-900 tw-placeholder-gray-500"
        />
        <div className="tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-3">
          <span className="material-symbols-outlined tw-text-gray-400 tw-text-lg">search</span>
        </div>
      </div>
      
      {/* Controls Row: Product Count + View Toggle */}
      <div className="tw-flex tw-items-center tw-justify-between">
        <div className="tw-text-xs tw-text-gray-500">
          {loading ? 'Searching...' : hasSearched ? `${products.length} product${products.length !== 1 ? 's' : ''} found` : 'Enter search term to find products'}
        </div>
        <div className="tw-flex tw-items-center tw-gap-1 tw-bg-gray-100 tw-rounded-lg tw-p-1">
          <button 
            onClick={() => setViewMode('row')} 
            className={`tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-md tw-transition-all tw-duration-200 hover:tw-text-gray-700 ${
              viewMode === 'row' 
                ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm' 
                : 'tw-text-gray-500'
            }`}
            title="Row view"
          >
            <span className="material-symbols-outlined tw-text-[16px]">view_list</span>
          </button>
          <button 
            onClick={() => setViewMode('grid')} 
            className={`tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-md tw-transition-all tw-duration-200 hover:tw-text-gray-700 ${
              viewMode === 'grid' 
                ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm' 
                : 'tw-text-gray-500'
            }`}
            title="Grid view"
          >
            <span className="material-symbols-outlined tw-text-[16px]">grid_view</span>
          </button>
        </div>
      </div>
    </>
  )

  // Footer content
  const footerContent = (
    <div className="tw-flex tw-items-center tw-justify-end tw-w-full tw-gap-3">
      <button
        onClick={onClose}
        className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-50 tw-transition-colors"
      >
        Cancel
      </button>
      <button
        onClick={handleConfirmSelection}
        disabled={!selectedProductId}
        className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-blue-600 tw-rounded-md hover:tw-bg-blue-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
      >
        {mode === 'link' ? 'Link Product' : 'Copy Product'}
      </button>
    </div>
  )

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      subtitle={modalDescription}
      size="xl"
      footer={footerContent}
      className={className}
      showCloseButton={true}
      customHeaderContent={customHeaderContent}
    >
      {/* Product Content */}
      <div className="tw-p-6">
        {error ? (
          // Error state
          <div className="tw-text-center tw-py-12">
            <span className="material-symbols-outlined tw-text-4xl tw-text-red-400 tw-mb-4 tw-block">error</span>
            <h3 className="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">Search Error</h3>
            <p className="tw-text-gray-600 tw-mb-4">{error}</p>
            <button 
              onClick={() => searchProducts(searchQuery)}
              className="tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded-md hover:tw-bg-blue-700 tw-transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : loading ? (
          // Loading state
          <div className="tw-text-center tw-py-12">
            <div className="tw-inline-block tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-blue-600 tw-mb-4"></div>
            <h3 className="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">Searching Products</h3>
            <p className="tw-text-gray-600">Please wait while we find products...</p>
          </div>
        ) : !hasSearched ? (
          // Initial empty state - prompt to search (No Search Query State)
          <div className="tw-text-center tw-py-12">
            <div className="tw-inline-flex tw-items-center tw-justify-center tw-w-20 tw-h-20 tw-bg-gray-100 tw-rounded-full tw-mb-4">
              <span className="material-symbols-outlined tw-text-[32px] tw-text-gray-400">inventory_2</span>
            </div>
            <h3 className="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">Browse Your Products</h3>
            <p className="tw-text-gray-500 tw-mb-6 tw-max-w-sm tw-mx-auto">
              Start typing in the search box to find products from your inventory.
            </p>
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-sm tw-text-gray-600">
              <span className="material-symbols-outlined tw-text-[16px]">info</span>
              <span>Search by product name or SKU</span>
            </div>
          </div>
        ) : products.length > 0 ? (
          // Products found
          <div className="tw-max-h-96 tw-overflow-y-auto">
            {/* Row View */}
            {viewMode === 'row' && (
              <div className="tw-space-y-3">
                {products.map((product) => (
                  <label
                    key={product.id}
                    className={`tw-flex tw-items-start tw-gap-4 tw-p-4 tw-border tw-border-gray-200 tw-rounded-lg hover:tw-bg-gray-50 tw-cursor-pointer tw-transition-colors ${
                      selectedProductId === product.id
                        ? 'selected-product'
                        : ''
                    }`}
                  >
                    <input
                      type="radio"
                      value={product.id}
                      checked={selectedProductId === product.id}
                      onChange={() => handleSelectProduct(product)}
                      className="tw-mt-1 tw-text-blue-600 focus:tw-ring-blue-500"
                    />
                    
                    <div className="tw-flex-1 tw-flex tw-items-start tw-gap-4">
                      <div className="tw-w-16 tw-h-16 tw-bg-gray-100 tw-rounded-lg tw-overflow-hidden tw-flex-shrink-0">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="tw-w-full tw-h-full tw-object-cover"
                        />
                      </div>
                      
                      <div className="tw-flex-1 tw-min-w-0">
                        <h4 className="tw-font-medium tw-text-gray-900 tw-text-sm tw-mb-1">
                          {product.name}
                        </h4>
                        <p className="tw-text-sm tw-text-gray-500 tw-mb-2">
                          SKU: {product.sku}
                        </p>
                        <div className="tw-flex tw-items-center tw-gap-4">
                          <span className="tw-text-sm tw-font-medium tw-text-gray-900">
                            {product.price}
                          </span>
                          <span className="tw-text-sm tw-text-gray-500">
                            Stock: {product.stock}
                          </span>
                        </div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
            
            {/* Grid View */}
            {viewMode === 'grid' && (
              <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-3">
                {products.map((product) => (
                  <label
                    key={product.id}
                    className={`tw-flex tw-items-start tw-gap-3 tw-p-3 tw-border tw-border-gray-200 tw-rounded-lg hover:tw-bg-gray-50 tw-cursor-pointer tw-transition-colors ${
                      selectedProductId === product.id
                        ? 'selected-product'
                        : ''
                    }`}
                  >
                    <input
                      type="radio"
                      value={product.id}
                      checked={selectedProductId === product.id}
                      onChange={() => handleSelectProduct(product)}
                      className="tw-mt-1 tw-text-blue-600 focus:tw-ring-blue-500"
                    />
                    
                    <div className="tw-flex-1 tw-min-w-0">
                      {/* Product Image */}
                      <div className="tw-w-12 tw-h-12 tw-bg-gray-100 tw-rounded-md tw-overflow-hidden tw-mb-2">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="tw-w-full tw-h-full tw-object-cover"
                        />
                      </div>
                      
                      {/* Product Info */}
                      <div className="tw-space-y-1">
                        <h4 className="tw-font-medium tw-text-gray-900 tw-text-xs tw-leading-tight tw-truncate">
                          {product.name}
                        </h4>
                        <p className="tw-text-xs tw-text-gray-500">
                          {product.sku}
                        </p>
                        <div className="tw-flex tw-items-center tw-justify-between">
                          <span className="tw-text-xs tw-font-semibold tw-text-gray-900">
                            {product.price}
                          </span>
                          <span className="tw-text-xs tw-text-gray-500">
                            {product.stock}
                          </span>
                        </div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        ) : (
          // No products found state (No Results State)
          <div className="tw-text-center tw-py-12">
            <div className="tw-inline-flex tw-items-center tw-justify-center tw-w-20 tw-h-20 tw-bg-gray-100 tw-rounded-full tw-mb-4">
              <span className="material-symbols-outlined tw-text-[32px] tw-text-gray-400">search_off</span>
            </div>
            <h3 className="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">No Products Found</h3>
            <p className="tw-text-gray-500 tw-mb-6 tw-max-w-sm tw-mx-auto">
              We couldn't find any products matching "<span className="tw-font-medium">{searchQuery}</span>".
            </p>
            <div className="tw-space-y-2">
              <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-sm tw-text-gray-600">
                <span className="material-symbols-outlined tw-text-[16px]">lightbulb</span>
                <span>Try different keywords or check your spelling</span>
              </div>
              <button 
                onClick={() => setSearchQuery('')}
                className="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-text-blue-600 hover:tw-text-blue-800 tw-font-medium tw-transition-colors"
              >
                <span className="material-symbols-outlined tw-text-[16px]">refresh</span>
                <span>Clear search</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </ModalContainer>
  )
}

export default MerchantProductBrowserModal